import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Button, Alert, Modal, Image, Spinner } from 'react-bootstrap'
import SweetAlert from 'react-bootstrap-sweetalert'
import CaptchaCanvas from "../../../components/CaptchaCanvas"
import RegistrasiService from '../../../services/registrasi/RegistrasiService'
import auth6 from '../../../assets/images/auth/06.png'

const ActivatePassphrase = ({ show, handleClose, redirectTo = '/setting/edit-pegawai' }) => {
  const navigate = useNavigate()

  // State Form
  const [newPassphrase, setNewPassphrase] = useState("")
  const [confirmPassphrase, setConfirmPassphrase] = useState("")
  const [message, setMessage] = useState("")
  const [variant, setVariant] = useState("success")
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false)
  const [showUpdateFailed, setShowUpdateFailed] = useState(false)
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [loading, setLoading] = useState(false)

  // State Captcha
  const [captchaText, setCaptchaText] = useState("")
  const [captchaInput, setCaptchaInput] = useState("")

  // State Rules
  const [passphraseRules, setPassphraseRules] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    symbol: false,
  })

  useEffect(() => {
    if (show) {
      generateCaptcha()
      setNewPassphrase("")
      setConfirmPassphrase("")
      setMessage("")
      setShowPassphrase(false)
    }
  }, [show])

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"
    const text = Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join("")
    setCaptchaText(text)
    setCaptchaInput("")
  }

  const evaluatePassphrase = (passphrase) => {
    const rules = {
      length: passphrase.length >= 8,
      upper: /[A-Z]/.test(passphrase),
      lower: /[a-z]/.test(passphrase),
      number: /[0-9]/.test(passphrase),
      symbol: /[^A-Za-z0-9]/.test(passphrase),
    }
    setPassphraseRules(rules)
  }

  const ChecklistValidasi = ({ rules }) => (
    <ul className="list-unstyled mt-2 mb-1">
      <li className={rules.length ? "text-success" : "text-danger"}>
        {rules.length ? "✔" : "✖"} Minimal 8 karakter
      </li>
      <li className={rules.upper ? "text-success" : "text-danger"}>
        {rules.upper ? "✔" : "✖"} Huruf kapital
      </li>
      <li className={rules.lower ? "text-success" : "text-danger"}>
        {rules.lower ? "✔" : "✖"} Huruf kecil
      </li>
      <li className={rules.number ? "text-success" : "text-danger"}>
        {rules.number ? "✔" : "✖"} Angka
      </li>
      <li className={rules.symbol ? "text-success" : "text-danger"}>
        {rules.symbol ? "✔" : "✖"} Simbol
      </li>
    </ul>
  )

  const getPassphraseStrength = () => {
    const passed = Object.values(passphraseRules).filter(Boolean).length
    return (passed / 5) * 100
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Identitas certificate tidak lagi dikirim dari client.
    // src-signature mengambil user dari JWT/SecurityContext.
    const token = localStorage.getItem('token')
    if (!token) {
      setMessage("Sesi login tidak ditemukan. Silakan login ulang.")
      setVariant("danger")
      setShowUpdateFailed(true)
      return
    }

    // Validasi Keamanan Passphrase
    if (getPassphraseStrength() < 100) {
      setMessage("Passphrase belum memenuhi seluruh kriteria keamanan")
      setVariant("danger")
      return
    }

    if (newPassphrase !== confirmPassphrase) {
      setMessage("Konfirmasi Passphrase tidak sama!")
      setVariant("danger")
      setShowUpdateFailed(true)
      return
    }

    // Validasi Captcha
    if (captchaInput.toUpperCase() !== captchaText.toUpperCase()) {
      setMessage("Kode Captcha tidak sesuai!")
      setVariant("danger")
      setShowUpdateFailed(true)
      generateCaptcha()
      return
    }

    try {
      setLoading(true)
      const registrasiService = new RegistrasiService()
      const res = await registrasiService.postSignature(newPassphrase)
      setMessage(res.data?.message || "Passphrase berhasil diaktifkan.")
      setVariant("success")
      setShowUpdateSuccess(true)
    } catch (err) {
      console.error("❌ Detail Error Axios:", err)

      let errorMessage = "Gagal mengaktifkan Passphrase."
      if (err.response) {
        errorMessage = err.response.data?.message || err.response.data?.error || `Error Server (${err.response.status})`
      } else if (err.request) {
        errorMessage = "Server tidak merespon. Periksa jaringan atau URL API backend."
      } else {
        errorMessage = err.message
      }

      setMessage(errorMessage)
      setVariant("danger")
      setShowUpdateFailed(true)
      generateCaptcha()
    } finally{
      setLoading(false)
    }
  }

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
        <Modal.Header 
          closeButton 
          closeVariant="white"
          className="p-0 border-0 position-relative overflow-hidden"
        >
          <Image 
            src={auth6} 
            alt="Header Background" 
            className="w-100 gradient-main" 
            style={{ height: '140px', objectFit: 'cover' }} 
          />

          <div 
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center p-4"
            style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.2))' }}
          >
            <Modal.Title className="text-white fw-bold">
              Aktivasi Passphrase
            </Modal.Title>
            
            <p className="text-white m-0 mt-1" style={{ fontSize: '0.85rem' }}>
              Passphrase digunakan untuk kemudahan pengesahan dokumen dalam Aplikasi SIAPMUTU.
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">
          {message && <Alert variant={variant}>{message}</Alert>}

          <Form onSubmit={handleSubmit}>
            {/* Input Passphrase Baru */}
            <Form.Group controlId="formNewPassphrase" className="mt-2">
              <Form.Label>Passphrase Baru</Form.Label>
              <div className="d-flex">
                <Form.Control
                  type={showPassphrase ? "text" : "password"}
                  value={newPassphrase}
                  placeholder="Masukkan Passphrase"
                  onChange={(e) => {
                    const value = e.target.value
                    setNewPassphrase(value)
                    evaluatePassphrase(value)
                  }}
                  isInvalid={newPassphrase.length > 0 && getPassphraseStrength() < 100}
                  required
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  className="ms-2"
                  onClick={() => setShowPassphrase(!showPassphrase)}
                >
                  {showPassphrase ? "🙈" : "👁"}
                </Button>
              </div>
            </Form.Group>

            {/* Validasi Checklist */}
            {newPassphrase && <ChecklistValidasi rules={passphraseRules} />}

            {/* Input Konfirmasi Passphrase */}
            <Form.Group controlId="formConfirmPassphrase" className="mt-3">
              <Form.Label>Konfirmasi Passphrase Baru</Form.Label>
              <Form.Control
                type="password"
                placeholder="Ulangi Passphrase"
                value={confirmPassphrase}
                onChange={(e) => setConfirmPassphrase(e.target.value)}
                required
              />
            </Form.Group>

            {/* Captcha */}
            <Form.Group controlId="formCaptcha" className="mt-3">
              <Form.Label>Kode Captcha</Form.Label>
              <div className="d-flex align-items-center mb-2">
                <CaptchaCanvas text={captchaText} />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  type="button"
                  onClick={generateCaptcha}
                  title="Refresh Captcha"
                >
                  🔁
                </Button>
              </div>
              <Form.Control
                type="text"
                placeholder="Masukkan kode captcha"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                required
              />
            </Form.Group>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" type="button" onClick={handleClose}>
                Batal
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading && <Spinner/>}Aktifkan Passphrase
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* SweetAlert Sukses */}
      {showUpdateSuccess && (
        <SweetAlert
          title="Berhasil"
          success
          onConfirm={() => {
            setShowUpdateSuccess(false)
            handleClose()
            navigate(redirectTo)
          }}
          btnSize="sm"
        >
          Passphrase Anda telah berhasil diaktifkan.
        </SweetAlert>
      )}

      {/* SweetAlert Gagal */}
      {showUpdateFailed && (
        <SweetAlert
          title="Gagal"
          danger
          onConfirm={() => setShowUpdateFailed(false)}
          btnSize="sm"
        >
          {message}
        </SweetAlert>
      )}
    </>
  )
}

export default ActivatePassphrase